import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Check, Clock, Zap, Star, Router, Printer, Flame, Shield, Loader2 } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { AuthContext, API } from '@/App';

const PricingInfo = () => {
  const [billingCycle, setBillingCycle] = useState('annual'); // 'monthly' or 'annual'
  const [loadingPlan, setLoadingPlan] = useState(null); // Track which plan is loading
  const [checkoutConfig, setCheckoutConfig] = useState(null);
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch checkout configuration on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/subscriptions/checkout-config`);
        setCheckoutConfig(response.data);
      } catch (error) {
        console.error('Failed to fetch checkout config:', error);
      }
    };
    fetchConfig();
  }, []);

  // Handle subscribe button click
  const handleSubscribe = async (planId, editionChoice = null, editionChoices = null) => {
    // Check if user is logged in
    if (!user || !token) {
      toast.info('Please log in to subscribe');
      navigate('/auth?redirect=/pricing');
      return;
    }

    setLoadingPlan(planId);

    try {
      const requestData = {
        plan: planId,
        edition_choice: editionChoice,
        edition_choices: editionChoices
      };

      const response = await axios.post(
        `${API}/subscriptions/create-checkout`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.checkout_url) {
        // Redirect to Lemon Squeezy checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to start checkout. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoadingPlan(null);
    }
  };

  // Check if a plan is available for checkout
  const isPlanAvailable = (planId) => {
    if (!checkoutConfig) return false;
    return checkoutConfig.available_plans?.[planId] === true;
  };

  // Check if checkout is enabled at all
  const isCheckoutEnabled = checkoutConfig?.checkout_enabled === true;

  const monthlyPlans = [
    {
      id: 'router_monthly',
      name: 'CNC Router',
      icon: Router,
      price: 12,
      description: 'Full CNC Router Edition access',
      color: 'orange',
      features: [
        'Feeds & Speeds Calculator',
        'Chipload Optimizer',
        'Material Database (50+ materials)',
        'Tool Library Management',
        'Troubleshooting Guide',
        'AI Assistant'
      ]
    },
    {
      id: 'laser_monthly',
      name: 'Laser Cutting',
      icon: Flame,
      price: 12,
      description: 'Full Laser Edition access',
      color: 'orange',
      features: [
        'Power/Speed Calculator',
        'Material Guide (44+ materials)',
        'CO2 & Diode Support',
        'Safety Warnings',
        'Troubleshooting Guide',
        'AI Assistant'
      ]
    },
    {
      id: 'printing_monthly',
      name: '3D Printing',
      icon: Printer,
      price: 12,
      description: 'Full 3D Printing Edition access',
      color: 'orange',
      features: [
        'Print Settings Calculator',
        'Filament Guide (19+ materials)',
        'Printer Setup Wizard',
        'Troubleshooting Guide',
        'Slicer Integration Help',
        'AI Assistant'
      ]
    }
  ];

  const annualPlans = [
    {
      id: 'single_annual',
      name: 'Single Edition',
      price: 99,
      pricePerMonth: 8.25,
      description: 'Any one edition (user chooses)',
      editions: 1,
      features: [
        'Choose: CNC Router, Laser, or 3D Printing',
        'Full access to all tools in your edition',
        'AI troubleshooting assistant',
        'Material databases',
        'Machine setup workflows',
        'Community features'
      ],
      badge: null
    },
    {
      id: 'full_annual',
      name: 'Full Access',
      price: 159,
      pricePerMonth: 13.25,
      description: 'All three editions included',
      editions: 3,
      features: [
        'CNC Router Edition',
        'Laser Cutting Edition',
        '3D Printing Edition',
        'Future additions included',
        'Priority support',
        'Early access to new features'
      ],
      badge: 'Best Value',
      recommended: true
    }
  ];

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.CONTACT_FEEDBACK)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="max-w-6xl mx-auto">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center p-3 bg-[#e67e22]/20 rounded-lg mb-4">
            <CreditCard className="w-8 h-8 text-[#e67e22]" />
          </div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">SUBSCRIPTION PLANS</div>
          <h1 
            className="text-3xl md:text-4xl font-bold text-[#e67e22] tracking-tight uppercase"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Simple, Flexible Pricing
          </h1>
          <p className="text-zinc-400 mt-2">Choose the plan that works best for your workshop</p>
        </div>

        {/* Beta Notice */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-8 text-center max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-2 text-green-400 mb-2">
            <Shield className="w-5 h-5" />
            <span className="font-semibold">Beta Access - All Features Available</span>
          </div>
          <p className="text-sm text-green-300/80">
            During beta, all features are accessible to everyone. Subscribe now to support development
            and lock in early pricing. Your subscription will activate when we exit beta.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-full font-medium transition-all ${
              billingCycle === 'monthly' 
                ? 'bg-[#e67e22] text-black' 
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-6 py-2 rounded-full font-medium transition-all ${
              billingCycle === 'annual' 
                ? 'bg-[#e67e22] text-black' 
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            Annual
            <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Save up to 63%</span>
          </button>
        </div>

        {/* Monthly Plans */}
        {billingCycle === 'monthly' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {monthlyPlans.map((plan) => (
              <div 
                key={plan.id}
                className="bg-black/70 backdrop-blur-sm border border-zinc-700 rounded-lg p-6 hover:border-[#e67e22]/50 transition-all"
              >
                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-[#e67e22]/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <plan.icon className="w-6 h-6 text-[#e67e22]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{plan.name}</h3>
                  <p className="text-sm text-zinc-500 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-[#e67e22]">${plan.price}</span>
                    <span className="text-zinc-500">/month</span>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-zinc-300 text-sm">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  data-testid={`subscribe-${plan.id}-btn`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loadingPlan === plan.id || !isPlanAvailable(plan.id)}
                  className={`w-full ${
                    isPlanAvailable(plan.id)
                      ? 'bg-[#e67e22] hover:bg-[#d35400] text-black'
                      : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                  }`}
                >
                  {loadingPlan === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : isPlanAvailable(plan.id) ? (
                    'Subscribe'
                  ) : (
                    'Coming Soon'
                  )}
                </Button>
                {!isPlanAvailable(plan.id) && (
                  <p className="text-xs text-zinc-600 text-center mt-2">Checkout not yet configured</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Annual Plans */}
        {billingCycle === 'annual' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-3xl mx-auto">
            {annualPlans.map((plan) => (
              <div 
                key={plan.id}
                className={`bg-black/70 backdrop-blur-sm rounded-lg p-6 relative transition-all ${
                  plan.recommended 
                    ? 'border-2 border-[#e67e22] shadow-[0_0_30px_rgba(230,126,34,0.2)]' 
                    : 'border border-zinc-700 hover:border-[#e67e22]/50'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase ${
                      plan.recommended 
                        ? 'bg-[#e67e22] text-black' 
                        : 'bg-zinc-700 text-white'
                    }`}>
                      {plan.badge}
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6 mt-2">
                  <h3 className="text-lg font-semibold text-white mb-1">{plan.name}</h3>
                  <p className="text-sm text-zinc-500 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-[#e67e22]">${plan.price}</span>
                    <span className="text-zinc-500">/year</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">(${plan.pricePerMonth.toFixed(2)}/month)</p>
                  
                  {/* Edition icons */}
                  <div className="flex items-center justify-center gap-2 mt-4">
                    {plan.editions >= 1 && (
                      <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center" title="CNC Router">
                        <Router className="w-4 h-4 text-[#e67e22]" />
                      </div>
                    )}
                    {plan.editions >= 2 && (
                      <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center" title="Laser">
                        <Flame className="w-4 h-4 text-[#e67e22]" />
                      </div>
                    )}
                    {plan.editions >= 3 && (
                      <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center" title="3D Printing">
                        <Printer className="w-4 h-4 text-[#e67e22]" />
                      </div>
                    )}
                  </div>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-zinc-300 text-sm">
                      {feature.includes('Future') || feature.includes('Priority') || feature.includes('Early') ? (
                        <Star className="w-4 h-4 text-[#e67e22] flex-shrink-0" />
                      ) : (
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      )}
                      <span className={feature.includes('Future') || feature.includes('Priority') || feature.includes('Early') ? 'text-[#e67e22]' : ''}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  data-testid={`subscribe-${plan.id}-btn`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loadingPlan === plan.id || !isPlanAvailable(plan.id)}
                  className={`w-full ${
                    plan.recommended && isPlanAvailable(plan.id)
                      ? 'bg-[#e67e22] hover:bg-[#d35400] text-black' 
                      : isPlanAvailable(plan.id)
                        ? 'bg-[#e67e22] hover:bg-[#d35400] text-black'
                        : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                  }`}
                >
                  {loadingPlan === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : isPlanAvailable(plan.id) ? (
                    'Subscribe'
                  ) : (
                    'Coming Soon'
                  )}
                </Button>
                {!isPlanAvailable(plan.id) && (
                  <p className="text-xs text-zinc-600 text-center mt-2">Checkout not yet configured</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Learn More Section */}
        <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-6 mb-8">
          <h3 className="text-xl font-semibold text-white mb-6 text-center">What's Included in Each Edition</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-zinc-900/50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Router className="w-5 h-5 text-[#e67e22]" />
                <h4 className="text-[#e67e22] font-semibold">CNC Router Edition</h4>
              </div>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li>• Feeds & Speeds Calculator</li>
                <li>• Chipload Optimizer</li>
                <li>• Material Database (50+ materials)</li>
                <li>• Tool Library Management</li>
                <li>• Troubleshooting Guide</li>
                <li>• AI Assistant</li>
              </ul>
            </div>
            
            <div className="p-4 bg-zinc-900/50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Printer className="w-5 h-5 text-[#e67e22]" />
                <h4 className="text-[#e67e22] font-semibold">3D Printing Edition</h4>
              </div>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li>• Print Settings Calculator</li>
                <li>• Filament Guide (19+ materials)</li>
                <li>• Printer Setup Wizard</li>
                <li>• Troubleshooting Guide</li>
                <li>• Slicer Integration Help</li>
                <li>• AI Assistant</li>
              </ul>
            </div>
            
            <div className="p-4 bg-zinc-900/50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-5 h-5 text-[#e67e22]" />
                <h4 className="text-[#e67e22] font-semibold">Laser Cutting Edition</h4>
              </div>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li>• Power/Speed Calculator</li>
                <li>• Material Guide (44+ materials)</li>
                <li>• CO2 & Diode Support</li>
                <li>• Safety Warnings</li>
                <li>• Troubleshooting Guide</li>
                <li>• AI Assistant</li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-6">Frequently Asked Questions</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-[#e67e22] font-medium mb-2">Can I cancel anytime?</h4>
              <p className="text-sm text-zinc-400">Yes, you can cancel your subscription at any time. You'll retain access until the end of your billing period.</p>
            </div>
            
            <div>
              <h4 className="text-[#e67e22] font-medium mb-2">Can I upgrade my plan?</h4>
              <p className="text-sm text-zinc-400">Absolutely! You can upgrade from a single edition to two-edition or full access anytime. We'll prorate the difference.</p>
            </div>
            
            <div>
              <h4 className="text-[#e67e22] font-medium mb-2">What are "future additions"?</h4>
              <p className="text-sm text-zinc-400">Full Access subscribers automatically get access to any new editions or major features we add, at no extra cost.</p>
            </div>
            
            <div>
              <h4 className="text-[#e67e22] font-medium mb-2">Is there a free trial?</h4>
              <p className="text-sm text-zinc-400">We're currently in beta testing. Apply to be a beta tester on our homepage for early free access!</p>
            </div>
            
            <div>
              <h4 className="text-[#e67e22] font-medium mb-2">How do payments work?</h4>
              <p className="text-sm text-zinc-400">Payments are processed securely through LemonSqueezy. We accept all major credit cards.</p>
            </div>
            
            <div>
              <h4 className="text-[#e67e22] font-medium mb-2">Which plan is best for me?</h4>
              <p className="text-sm text-zinc-400">If you use multiple disciplines, the Full Access annual plan is the best value. For single-discipline users, choose the edition you need.</p>
            </div>
          </div>
        </div>

        <p className="text-center text-zinc-500 text-sm mt-8">
          Questions? Contact us at <a href="mailto:Andre@cnc-helpdesk.com" className="text-[#e67e22] hover:underline">Andre@cnc-helpdesk.com</a>
        </p>
      </div>
    </div>
  );
};

export default PricingInfo;
